import axios from "axios";
import { getDocs, limit, orderBy, query, startAfter } from "firebase/firestore";
import {
  getCollectionRef,
  toDocumentData,
  fromDocumentData,
} from "./FirebaseRepository";
import MeasurementRowModel from "../models/MeasurementRowModel";

class MeasurementRowRepository {
  private path = (contractUid: string, measurementUid: string) =>
    `cfContract/${contractUid}/measurements/${measurementUid}/wbs`;

  async getAllRowsFromDataBase(
    contractUid: string,
    measurementUid: string
  ): Promise<MeasurementRowModel[]> {
    const collection = getCollectionRef(
      this.path(contractUid, measurementUid),
      converter(contractUid, measurementUid)
    );
    return getDocs(collection).then((snapshot) =>
      snapshot.docs.map((doc) => doc.data() as MeasurementRowModel)
    );
  }

  getMeasurementsRowsQuery(
    contractUid: string,
    measurementUid: string,
    startAt?: string,
    limitOf: number = 1000
  ) {
    const collection = getCollectionRef(
      this.path(contractUid, measurementUid),
      converter(contractUid, measurementUid)
    );
    if (startAt)
      return query(
        collection,
        orderBy("structured"),
        startAfter(startAt),
        limit(limitOf)
      );
    return query(collection, orderBy("structured"), limit(limitOf));
  }

  async deleteMeasurementRows(
    contractUid: string,
    measurementUid: string
  ): Promise<any> {
    const data = { contractUid, measurementUid };
    return axios
      .post(`${process.env.REACT_APP_DELETE_MENSUREMENT_ROWS}`, data)
      .catch((error) => {
        console.error(error);
        throw new Error(
          `Error on delete measurements rows: ${error.response.data?.error}`
        );
      });
  }

  async fetchMeasurementRows(
    contractUid: string,
    measurementUid: string
  ): Promise<any> {
    const data = { IDContract: contractUid, IDMeasurement: measurementUid };

    return axios
      .post(`${process.env.REACT_APP_GET_MENSUREMENT_WBS}`, data)
      .then((res) => {
        const data = res.data?.data || [];
        return data.map((row) => {
          return MeasurementRowModel.create(row.id, {
            ...row.data,
            contractUid,
            measurementUid,
          });
        });
      })
      .catch((err) => {
        console.error(err);
        console.error(
          "Error on function fetchMeasurementRows",
          err.response.data?.error
        );
      });
  }

  async fetchAllMeasurementsRows(
    contractUid: string
  ): Promise<{ uid: string; wbs: MeasurementRowModel[] }[]> {
    const data = { IDContract: contractUid };
    return axios
      .post(`${process.env.REACT_APP_GET_MENSUREMENT}`, data)
      .then((res) => {
        const data = res.data?.data || [];
        return data.map((measurement) => {
          const measurementUid = measurement.uid;
          const wbs = measurement.wbs.map((row) => {
            return MeasurementRowModel.create(row.id, {
              ...row.data,
              contractUid,
              measurementUid,
            });
          });

          return {
            uid: measurementUid,
            wbs,
          };
        });
      })
      .catch((err) => {
        console.error(err);
        console.error(
          "Error on function fetchAllMeasurementsRows",
          err.response.data?.error
        );
      });
  }
}

// Firestore data converter
const converter = (contractUid, measurementUid) => ({
  toFirestore: (measurementRow: MeasurementRowModel) => {
    const data = measurementRow.toSave();
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);
    return MeasurementRowModel.create(snapshot.id, {
      ...data,
      contractUid,
      measurementUid,
    });
  },
});

export default new MeasurementRowRepository();
